import React from 'react';
import {
  Container,
  Paper,
  Title,
  Badge,
  Group,
  Stack,
  Loader,
} from '@mantine/core';
import { ErrorDisplay } from '../common/ErrorDisplay';
import { useItineraryOverview } from '../../hooks/useItineraryOverview';
import { EditableSummary } from './EditableSummary';
import { TravelerItinerary } from './TravelerItinerary';
import { CombinedTimeline } from './CombinedTimeline';

export interface ItineraryOverviewProps {
  tripId: string;
}

export const ItineraryOverview: React.FC<ItineraryOverviewProps> = ({
  tripId,
}) => {
  const {
    tripQuery,
    trip,
    user,
    error,
    headerForm,
    setHeaderForm,
    isAdmin,
    travelers,
    activeTravelerId,
    activeTraveler,
    currentUserMembership,
    showNewVisitRow,
    busy,
    formatSummaryDate,
    setActiveTravelerId,
    setShowNewVisitRow,
    handleResetHeader,
    handleSaveShared,
    handleCreateMembership,
    handleAddVisit,
    handleUpdateVisit,
    handleDeleteVisit,
    isSavingTrip,
    isCreatingMembership,
    isAddingVisit,
    isUpdatingVisit,
    isDeletingVisit,
  } = useItineraryOverview(tripId);

  if (tripQuery.isLoading) {
    return (
      <Container size="lg" mt="xl" data-cy="trip-itinerary-page">
        <Loader />
      </Container>
    );
  }

  if (tripQuery.isError || !trip) {
    return (
      <Container size="lg" mt="xl" data-cy="trip-itinerary-page">
        {error && <ErrorDisplay error={error} />}
      </Container>
    );
  }

  return (
    <Container size="lg" mt="xl" mb="xl" data-cy="trip-itinerary-page">
      <Stack gap="lg">
        {error && <ErrorDisplay error={error} />}

        <EditableSummary
          headerForm={headerForm}
          isAdmin={isAdmin}
          busy={busy}
          isSaving={isSavingTrip}
          onHeaderFormChange={setHeaderForm}
          onReset={handleResetHeader}
          onSave={handleSaveShared}
        />

        <Group gap="sm" wrap="wrap" data-cy="itinerary-summary-badges">
          {trip.derivedStartDate && trip.derivedEndDate && (
            <Badge variant="light" color="horizon" size="lg">
              {formatSummaryDate(trip.derivedStartDate)} –{' '}
              {formatSummaryDate(trip.derivedEndDate)}
            </Badge>
          )}
          <Badge variant="light" color="horizon" size="lg">
            {trip.totalNights} total nights
          </Badge>
          <Badge variant="light" color="sand" size="lg">
            {travelers.length} traveler{travelers.length === 1 ? '' : 's'}
          </Badge>
          {trip.derivedCities.length > 0 && (
            <Badge variant="outline" size="lg">
              {trip.derivedCities.join(' · ')}
            </Badge>
          )}
        </Group>

        <TravelerItinerary
          travelers={travelers}
          activeTravelerId={activeTravelerId}
          activeTraveler={activeTraveler}
          currentUser={user}
          hasCurrentUserMembership={currentUserMembership != null}
          showNewVisitRow={showNewVisitRow}
          busy={busy}
          isCreatingMembership={isCreatingMembership}
          isAddingVisit={isAddingVisit}
          isUpdatingVisit={isUpdatingVisit}
          isDeletingVisit={isDeletingVisit}
          onSelectTraveler={setActiveTravelerId}
          onCreateMembership={handleCreateMembership}
          onShowNewVisitRow={() => setShowNewVisitRow(true)}
          onAddVisit={handleAddVisit}
          onUpdateVisit={handleUpdateVisit}
          onDeleteVisit={handleDeleteVisit}
        />

        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={3} mb="md">
            Combined Timeline
          </Title>
          <CombinedTimeline
            travelers={travelers}
            derivedStartDate={trip.derivedStartDate}
            derivedEndDate={trip.derivedEndDate}
          />
        </Paper>
      </Stack>
    </Container>
  );
};

export default ItineraryOverview;
